import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';
import { TFunction } from 'i18next';

import { useCapabilities } from 'hooks/useCapabilities/useCapabilities';
import { useFlattenDataAndUIConfig } from 'hooks/useFlattenDataAndUIConfig';
import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { useMapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { useMessageWithProcessSpecification } from 'services/repositories/messages/messages';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { useInitWebEDIStore } from 'services/webedi/webedi';
import { usePackingListStore } from 'stores/packingList/packingList';
import { setDestinationProcess, setInitialCanonical, setProcessSpecificationFields } from 'stores/webedi/webedi';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { getOriginalPDF } from 'support/helpers/messages/messages';
import { routeToPage, routeToTransactionPage } from 'support/helpers/navigation/navigation';
import { useDocumentCreationRelationship } from 'support/helpers/relationships/relationships';
import { RouteParams } from 'support/helpers/types/types';
import {
  DocumentType,
  DocumentTypeWithVirtual,
  MessageV2DTO,
  PartnerDTO,
  ProcessSpecificationDTO,
  RelationshipV2,
  TransactionInternalType,
  TransactionOriginApp,
  TransactionsRoutes,
  UserDTO,
} from 'support/types';
import { Button } from 'components/Form/Button';
import { UnsavedChangesModal } from 'components/Navigation/UnsavedChangesModal/UnsavedChangesModal';
import { Page } from 'components/Page/Page';
import { deriveDocumentDisplayConfig } from 'components/WebEDI/DocumentDisplayConfig/deriveDocumentDisplayConfig';
import { DocumentDisplayConfigProvider } from 'components/WebEDI/DocumentDisplayConfig/DocumentDisplayConfigContext';
import { PDFReferenceSidebar } from 'components/WebEDI/WebEDISidebar/components/PDFReferenceSidebar';

import { useEditAndReprocess } from './hooks/useEditAndReprocess/useEditAndReprocess';
import { SendConfirmationModal } from '../components/CreateDocument/components/SendConfirmationModal/SendConfirmationModal';
import { EditMessageContent } from '../EditMessageContent/EditMessageContent';
import { revealPartyFields } from '../helpers';
import { UnknownTypePlaceholder } from '../TransactionViewPage/MessageViewPage/UnknownTypePlaceholder';

export function ReprocessDocument() {
  useInitWebEDIStore();
  const { id } = useParams<{ id: string }>();
  const { data: currentPartner } = useCurrentPartner();
  const { data: currentUser } = useCurrentUser();
  const { data: message, isError } = useMessageWithProcessSpecification({
    variables: {
      messageId: id,
    },
  });

  useCapabilities({ messageId: id });

  const nonVirtualDocumentType = useMapVirtualToActualDocumentType(message?.data?.type as DocumentTypeWithVirtual);

  const relationship = useDocumentCreationRelationship({
    sourceMessageId: message?.data?.id,
    documentType: nonVirtualDocumentType,
  });

  const fields = useMemo(() => {
    if (!message?.fields || !currentUser?.isAdmin) {
      return message?.fields;
    }
    return revealPartyFields(message.fields);
  }, [message?.fields, currentUser?.isAdmin]);

  const {
    data,
    uiConfig,
    isLoading: isLoadingDataChecks,
  } = useFlattenDataAndUIConfig(fields, message?.data?.canonical);

  useEffect(() => {
    setProcessSpecificationFields(fields);

    if (message?.data.canonical) {
      setInitialCanonical(message?.data.canonical);
    }

    if (message?.process) {
      setDestinationProcess(message?.process);
    }
  }, [message, fields]);

  if (!id) {
    return null;
  }

  if (isError) {
    return <UnknownTypePlaceholder messageId={id} />;
  }

  if (isLoadingDataChecks || !relationship) {
    return <Page isLoading={true}>{null}</Page>;
  }

  return (
    <ReprocessDocumentPageUI
      message={message?.data}
      messageOriginApp={message?.origin_app}
      data={data}
      currentPartner={currentPartner}
      uiConfig={uiConfig}
      processSpecFields={fields}
      currentUser={currentUser}
      relationship={relationship}
      documentType={nonVirtualDocumentType}
    />
  );
}

type ReprocessDocumentPageUIProps = {
  message: MessageV2DTO | undefined;
  messageOriginApp: TransactionOriginApp | undefined;
  data: Record<string, unknown>;
  currentPartner: PartnerDTO | undefined;
  uiConfig: Array<ui.Section> | undefined;
  processSpecFields: ProcessSpecificationDTO['fields'] | undefined;
  currentUser: UserDTO | undefined;
  relationship: RelationshipV2;
  documentType: DocumentType | undefined;
};

export const ReprocessDocumentPageUI = ({
  message,
  messageOriginApp,
  data,
  uiConfig,
  currentPartner,
  processSpecFields,
  currentUser,
  relationship,
  documentType,
}: ReprocessDocumentPageUIProps) => {
  const { t } = useTranslation();
  const packingListEnabled = usePackingListStore((state) => state.enabled);
  const navigate = useNavigate();

  const canEditMessage = Boolean(message?.sender_partner?.id && message.sender_partner.id === currentPartner?.id);
  const displayConfig = deriveDocumentDisplayConfig(message);
  const originalPDF = getOriginalPDF(messageOriginApp, message);

  const handleReprocessSuccess = useCallback(
    (response: { transactionId: string; transactionType: TransactionInternalType }) => {
      navigate(routeToTransactionPage(response.transactionType, response.transactionId), {
        state: { keepBreadcrumbs: true },
      });
    },
    [navigate],
  );

  const { reprocessMessage, isReprocessing, isSuccess } = useEditAndReprocess({
    message,
    processSpecificationFields: processSpecFields,
    relationship,
    documentType,
    onSuccess: handleReprocessSuccess,
  });

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<Record<string, unknown> | null>(null);

  const onSubmit = useCallback((formValues: Record<string, unknown>) => {
    setPendingFormValues(formValues);
    setConfirmationModalOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    setConfirmationModalOpen(false);
    if (pendingFormValues) {
      reprocessMessage(pendingFormValues);
    }
  }, [reprocessMessage, pendingFormValues]);

  const handleCancelConfirmation = useCallback(() => setConfirmationModalOpen(false), []);

  const handleCancel = useCallback(() => {
    if (message?.id) {
      navigate(routeToTransactionPage(TransactionInternalType.message, message.id));
    }
  }, [navigate, message?.id]);

  return (
    <DocumentDisplayConfigProvider config={displayConfig}>
      <EditMessageContent
        message={message}
        data={data}
        uiConfig={uiConfig}
        processSpecFields={processSpecFields}
        currentPartner={currentPartner}
        currentUser={currentUser}
        canEditMessage={canEditMessage}
        onSubmit={onSubmit}
        isSubmitting={isReprocessing}
        actionBar={
          <div className="flex items-center gap-2">
            <Button
              variant="minimal"
              disabled={isReprocessing}
              onClick={handleCancel}
              analyticsId="webedi:reprocess_cancel"
            >
              {t('webedi:createDocument.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="webedi-document"
              RightIcon={PaperAirplaneIcon}
              loading={isReprocessing}
              analyticsId="webedi:reprocess_send"
            >
              {t(packingListEnabled ? 'webedi:createDocument.submitWithLabels' : 'webedi:createDocument.submit')}
            </Button>
          </div>
        }
        sidebar={
          originalPDF ? <PDFReferenceSidebar pdfUrl={originalPDF.url} filename={originalPDF.filename} /> : undefined
        }
        modals={
          <>
            <UnsavedChangesModal shouldBlock={!isSuccess} />
            <SendConfirmationModal
              onConfirm={handleConfirm}
              open={confirmationModalOpen}
              onCancel={handleCancelConfirmation}
              documentType={message?.type as DocumentTypeWithVirtual}
              receiverTradePartner={message?.receiver_partner ?? undefined}
            />
          </>
        }
      />
    </DocumentDisplayConfigProvider>
  );
};

ReprocessDocument.loader = createLoader(
  ({ params }) => useMessageWithProcessSpecification.getKey({ messageId: params.id }),
  useMessageWithProcessSpecification.queryFn,
);

type ResponseType = Awaited<ReturnType<typeof useMessageWithProcessSpecification.queryFn>>;
ReprocessDocument.handle = createHandle<ResponseType, RouteParams<TransactionsRoutes.reprocess>>((params, data) => ({
  label: (t: TFunction) =>
    t('webedi:reprocessDocument.breadcrumb', {
      externalReference: data?.data.external_message_ref || EMPTY_FIELD,
      documentType: t(`common:messageTypes.singular.${data?.data.type || DocumentType.unknown}`),
    }),
  to: routeToPage(TransactionsRoutes.reprocess, { id: params.id }),
  key: `reprocess-document`,
}));

/**
 * i18n keys
 * t('webedi:createDocument.submit')
 * t('webedi:createDocument.submitWithLabels')
 */
